import React, { useState } from 'react';
import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import { useStyles } from './TemperaturesSection';
import { useTranslation } from 'react-i18next';
import { TEMPERATURETYPES } from '../../types/temperatureTypes';

export const TemperatureFormController = ({
  temperatures,
  setTemperatures,
  isRequired = false,
  type,
  disabled = false,
  errorValidations = {
    tempMin: {},
    tempMinF: {},
    tempMax: {},
    tempMaxF: {},
  },
  setErrors = (e: any) => {
    // No-op: Default implementation
  },
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [tempSelectionType, setTempSelectionType] = useState('');
  const step = 0.1;

  const filterKeyPress = e => {
    const char = e?.key;
    const deny = ['e', 'E', '+'];
    if (deny.includes(char)) {
      e.preventDefault();
    }
  };
  const toCelcius = fDeg => {
    const numDeg = Number(fDeg);
    return (((numDeg - 32) * 5) / 9).toFixed(1);
  };

  const toFarenheit = cDeg => {
    const numDeg = Number(cDeg);
    return ((numDeg * 9) / 5 + 32).toFixed(1);
  };

  const deleteError = (error, ...temps) => {
    temps.forEach(t => {
      const tempObj = errorValidations?.[t];
      if (tempObj) {
        delete tempObj[error];
      }
    });

    if (setErrors) {
      setErrors({ ...errorValidations });
    }
  };

  const addError = (error, ...temps) => {
    temps.forEach(
      t =>
        (errorValidations[t] = {
          ...errorValidations[t],
          [error]: true,
        }),
    );

    if (setErrors) {
      setErrors({ ...errorValidations });
    }
  };

  const validateMinGreaterMax = (min, max) => {
    if (+min >= +max) {
      addError('minMax', 'tempMin', 'tempMinF', 'tempMax', 'tempMaxF');
    } else {
      deleteError('minMax', 'tempMin', 'tempMinF', 'tempMax', 'tempMaxF');
    }
  };

  const validateOneDecimalOnly = (value, temperature) => {
    const decimals = value.toString().split('.')[1]?.length || 0;
    if (decimals > 1) {
      addError('oneDecimal', temperature);
    } else {
      deleteError('oneDecimal', temperature);
    }
  };

  const handleTemperaturesChange = (isFarenheit, event, type) => {
    const selectionType = isFarenheit ? 'F' : 'C';

    /* start - validations for corrective and disposal temperatures */
    /* using type property to check temperatures because names may vary with languages */
    if (
      type === TEMPERATURETYPES.CORRECTIVE ||
      type === TEMPERATURETYPES.DISPOSAL
    ) {
      // validation for not more than 1 decimal values
      const inputTemp = event.target.value;
      event.target.value =
        inputTemp.indexOf('.') >= 0
          ? inputTemp.substr(0, inputTemp.indexOf('.')) +
            inputTemp.substr(inputTemp.indexOf('.'), 2)
          : inputTemp;

      // validation for Farenheit values not less than 0
      if (isFarenheit && event.target.value < 0) {
        event.target.value = 0;
      }

      // validation for Celcius values not more than -17.7 degrees
      if (!isFarenheit && event.target.value < -17.7) {
        event.target.value = -17.7;
      }
      /* end - validations for corrective and disposal temperatures */
    } else {
      if (!isFarenheit && event.target.value < -572.8) {
        event.target.value = -572.8;
      }

      if (isFarenheit && event.target.value < -999) {
        event.target.value = -999;
      }

      if (!isFarenheit && event.target.value > 537.2) {
        event.target.value = 537.2;
      }

      if (isFarenheit && event.target.value > 999) {
        event.target.value = 999;
      }
    }

    if (!tempSelectionType) {
      setTempSelectionType(selectionType);
    }

    const newTemps = {
      ...temperatures,
      [event.target.name]: event.target.value,
    };

    switch (event.target.name) {
      case 'tempMin':
        deleteError('required', 'tempMin', 'tempMinF');
        newTemps.tempMinF = event.target.value
          ? String(toFarenheit(event.target.value))
          : '';
        if (temperatures.tempMax || temperatures.tempMax === 0) {
          validateMinGreaterMax(event.target.value, temperatures.tempMax);
        }
        break;

      case 'tempMinF':
        deleteError('required', 'tempMin', 'tempMinF');
        newTemps.tempMin = event.target.value
          ? String(toCelcius(event.target.value))
          : '';
        if (temperatures.tempMaxF || temperatures.tempMaxF === 0) {
          validateMinGreaterMax(event.target.value, temperatures.tempMaxF);
        }
        break;

      case 'tempMax':
        deleteError('required', 'tempMax', 'tempMaxF');
        newTemps.tempMaxF = event.target.value
          ? String(toFarenheit(event.target.value))
          : '';
        if (temperatures.tempMin || temperatures.tempMin === 0) {
          validateMinGreaterMax(temperatures.tempMin, event.target.value);
        }
        break;

      case 'tempMaxF':
        deleteError('required', 'tempMax', 'tempMaxF');
        newTemps.tempMax = event.target.value
          ? String(toCelcius(event.target.value))
          : '';
        if (temperatures.tempMinF || temperatures.tempMinF === 0) {
          validateMinGreaterMax(temperatures.tempMinF, event.target.value);
        }
        break;
    }

    switch (tempSelectionType) {
      case 'C':
        if (newTemps.tempMin == '' && newTemps.tempMax == '') {
          setTempSelectionType('');
        }
        break;

      case 'F':
        if (newTemps.tempMinF == '' && newTemps.tempMaxF == '') {
          setTempSelectionType('');
        }
        break;
    }

    // Validate only one decimal allowed
    validateOneDecimalOnly(event.target.value, event.target.name);
    setTemperatures(newTemps, selectionType);
  };

  const validateRequired = (event, t1, t2) => {
    if (!event.target.value && event.target.value !== 0) {
      addError('required', t1, t2);
    } else {
      deleteError('required', t1, t2);
    }
  };

  const hasError = temp => {
    return (
      temp in errorValidations &&
      Object.keys(errorValidations?.[temp]).some(e => errorValidations[temp][e])
    );
  };

  return (
    <Grid container>
      <Grid item xs={5}>
        <Typography style={{ marginTop: 10 }}>
          {t('commodities.minimumTemperature')}
        </Typography>
      </Grid>
      <Grid item xs={3} style={{ textAlign: 'right' }}>
        <FormControl style={{ width: 150 }} size="small">
          <OutlinedInput
            disabled={tempSelectionType == 'F' || disabled}
            className={tempSelectionType == 'F' ? classes.disabled : ''}
            id="outlined-adornment-password"
            type="number"
            value={temperatures.tempMin}
            name="tempMin"
            error={hasError('tempMin')}
            required={isRequired}
            onKeyDown={clickEvent => filterKeyPress(clickEvent)}
            onChange={e => handleTemperaturesChange(false, e, type)}
            inputProps={{
              min: -572.8,
              step,
              max: temperatures.tempMax || 537.2,
            }}
            onBlur={e => validateRequired(e, 'tempMin', 'tempMinF')}
            endAdornment={<InputAdornment position="end">°C</InputAdornment>}
          />
        </FormControl>
      </Grid>
      <Grid item xs={1} style={{ textAlign: 'center', marginTop: 5 }}>
        <Typography>/</Typography>
      </Grid>
      <Grid item xs={3} style={{ alignContent: 'start' }}>
        <FormControl size="small" style={{ width: 150 }}>
          <OutlinedInput
            disabled={tempSelectionType == 'C' || disabled}
            className={tempSelectionType == 'C' ? classes.disabled : ''}
            id="outlined-adornment-password"
            type="number"
            value={temperatures.tempMinF}
            name="tempMinF"
            error={hasError('tempMinF')}
            onChange={e => handleTemperaturesChange(true, e, type)}
            onKeyDown={clickEvent => filterKeyPress(clickEvent)}
            required={isRequired}
            inputProps={{ min: -999, step, max: temperatures.tempMaxF }}
            onBlur={e => validateRequired(e, 'tempMin', 'tempMinF')}
            endAdornment={<InputAdornment position="end">°F</InputAdornment>}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <div style={{ height: 20 }} />
      </Grid>
      <Grid item xs={5}>
        <Typography style={{ marginTop: 10 }}>
          {t('commodities.maximumTemperature')}
        </Typography>
      </Grid>
      <Grid item xs={3} style={{ textAlign: 'right' }}>
        <FormControl style={{ width: 150 }} size="small">
          <OutlinedInput
            disabled={tempSelectionType == 'F' || disabled}
            className={tempSelectionType == 'F' ? classes.disabled : ''}
            id="outlined-adornment-password"
            type="number"
            value={temperatures.tempMax}
            name="tempMax"
            error={hasError('tempMax')}
            required={isRequired}
            onKeyDown={clickEvent => filterKeyPress(clickEvent)}
            inputProps={{
              min: temperatures.tempMin || -572.8,
              step,
              max: 537.2,
            }}
            onChange={e => handleTemperaturesChange(false, e, type)}
            onBlur={e => validateRequired(e, 'tempMax', 'tempMaxF')}
            endAdornment={<InputAdornment position="end">°C</InputAdornment>}
          />
        </FormControl>
      </Grid>
      <Grid item xs={1} style={{ textAlign: 'center', marginTop: 5 }}>
        <Typography>/</Typography>
      </Grid>
      <Grid item xs={3} style={{ alignContent: 'start' }}>
        <FormControl size="small" style={{ width: 150 }}>
          <OutlinedInput
            disabled={tempSelectionType == 'C' || disabled}
            className={tempSelectionType == 'C' ? classes.disabled : ''}
            id="outlined-adornment-password"
            type="number"
            value={temperatures.tempMaxF}
            name="tempMaxF"
            error={hasError('tempMaxF')}
            required={isRequired}
            onChange={e => handleTemperaturesChange(true, e, type)}
            onKeyDown={clickEvent => filterKeyPress(clickEvent)}
            inputProps={{ min: temperatures.tempMinF || 0, max: 999, step }}
            onBlur={e => validateRequired(e, 'tempMax', 'tempMaxF')}
            endAdornment={<InputAdornment position="end">°F</InputAdornment>}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
};
